<template>
  <div class="custom-container">
    <b-img
      v-if="!requestData"
      :src="bgImg"
      class="img-bg"
    />
    <div v-show="!requestData" class="auth-inner py-2 with-opacity">
      <!-- Loading -->
      <loading
        v-if="isLoading || isSubmitting"
        :active="true"
        :is-full-page="true"
        color="#498ceb"
      />

      <b-card v-if="!isLoading" class="mb-0 card-width" style="background-color: rgba(255, 255, 255, 0.9);">
        <!-- Logo -->
        <b-link class="brand-logo d-flex justify-content-center mb-2 align-items-center">
          <add-co-logo />
          <h2 class="text-primary font-weight-bolder custom-h2">
            AddCo
          </h2>
        </b-link>

        <!-- Not found -->
        <b-card v-if="clientNotFound">
          <h2 class="text-danger">
            {{ $t('client_not_found_check_url') }}
          </h2>
        </b-card>

        <div v-else>
          <b-row>
            <!-- New request -->
            <b-col cols="12" md="6" class="px-3 responsive-border">
              <b-card-title class="mb-1">
                {{ $t('arsopbl.new_request') }}
              </b-card-title>
              <b-card-text class="mb-2">
                {{ $t('arsopbl.new_request_description') }}
              </b-card-text>
              <b-button variant="primary" block @click="goToNewRequest">
                {{ $t('arsopbl.submit_request') }}
              </b-button>
              <hr class="d-md-none my-2">
            </b-col>

            <!-- Follow up -->
            <b-col cols="12" md="6" class="px-3">
              <b-card-title class="mb-1">
                {{ $t('arsopbl.follow_up_title') }}
              </b-card-title>
              <b-card-text class="mb-2">
                {{ $t('arsopbl.follow_up_description') }}
              </b-card-text>

              <validation-observer ref="followUpForm" #default="{ invalid }">
                <b-form class="mt-2" @submit.prevent="submitFollowUp">
                  <!-- Case number -->
                  <b-form-group :label="$t('arsopbl.case_number')">
                    <validation-provider :name="$t('arsopbl.case_number')" rules="required" #default="{ errors }">
                      <b-form-input
                        v-model="caseNumber"
                        :placeholder="$t('arsopbl.placeholder_case_number')"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <!-- Token -->
                  <b-form-group :label="$t('arsopbl.follow_up_token')">
                    <validation-provider :name="$t('arsopbl.follow_up_token')" rules="required" #default="{ errors }">
                      <b-form-input
                        v-model="followUpToken"
                        :placeholder="$t('arsopbl.placeholder_token')"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-button variant="primary" type="submit" class="mt-1" block :disabled="invalid || isSubmitting">
                    {{ $t('arsopbl.check_status') }}
                  </b-button>
                </b-form>
              </validation-observer>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>

    <!-- Request status display -->
    <div v-if="requestData" class="status-container">
      <b-card>
        <b-button variant="secondary" @click="requestData = null">
          <span class="mr-1">&#11148;</span>{{ $t('Back') }}
        </b-button>
      </b-card>

      <b-card>
        <h4 class="mb-2">{{ $t('arsopbl.request_status') }}</h4>

        <b-row>
          <b-col md="4">
            <strong>{{ $t('arsopbl.case_number') }}:</strong>
            <p>{{ requestData.caseNumber }}</p>
          </b-col>
          <b-col md="4">
            <strong>{{ $t('arsopbl.right_exercised') }}:</strong>
            <p>{{ $t(`arsopbl.right_${requestData.rightCode.toLowerCase()}`) }} ({{ requestData.rightCode }})</p>
          </b-col>
          <b-col md="4">
            <strong>{{ $t('arsopbl.submission_date') }}:</strong>
            <p>{{ formatDate(requestData.createdAt) }}</p>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <strong>{{ $t('arsopbl.current_stage') }}:</strong>
            <b-badge :variant="getStageVariant(requestData.stage)">
              {{ $t(`arsopbl.stage_${requestData.stage}`) }}
            </b-badge>
          </b-col>
          <b-col md="4">
            <strong>{{ $t('arsopbl.estimated_response') }}:</strong>
            <p>{{ estimatedResponseDate }}</p>
          </b-col>
          <b-col md="4">
            <strong>{{ $t('arsopbl.status') }}:</strong>
            <b-badge :variant="getStatusVariant(requestData.status)">
              {{ $t(`arsopbl.status_${requestData.status}`) }}
            </b-badge>
          </b-col>
        </b-row>

        <!-- Response (when resolved) -->
        <div v-if="requestData.stage === 'closure' && requestData.formalResponse">
          <hr>
          <h5>{{ $t('arsopbl.formal_response') }}</h5>
          <div class="border p-2 rounded" v-html="requestData.formalResponse"></div>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { ValidationObserver, ValidationProvider, localize } from 'vee-validate'
import { BButton, BForm, BFormInput, BFormGroup, BCard, BCardTitle, BCardText, BLink, BImg, BBadge } from 'bootstrap-vue'
import { required } from '@validations'
import { useRouter } from '@core/utils/utils'
import AddCoLogo from '@core/layouts/components/Logo.vue'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import i18n from '@/libs/i18n'
import useNotifications from '@/composables/useNotifications'
import { useLambda } from '@/views/habit/awsLambda'
import bannerDefault from '@/assets/images/banner/complaint-default.png'

export default {
  components: {
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BCard,
    BCardTitle,
    BCardText,
    BLink,
    BImg,
    BBadge,
    AddCoLogo,
    ValidationProvider,
    ValidationObserver,
    Loading,
  },
  setup() {
    const { showErrorMessage } = useNotifications()
    const { route, router } = useRouter()
    const { getItem } = useLambda()
    const { clientPublicId } = route.value.params

    const clientData = ref({})
    const clientNotFound = ref(false)
    const isLoading = ref(true)
    const isSubmitting = ref(false)
    const caseNumber = ref(route.value.query?.token ? '' : '')
    const followUpToken = ref(route.value.query?.token || '')
    const requestData = ref(null)
    const bgImg = bannerDefault

    const estimatedResponseDate = computed(() => {
      if (!requestData.value?.createdAt) return '-'
      const created = new Date(requestData.value.createdAt?.$date || requestData.value.createdAt)
      let businessDays = 0
      const current = new Date(created)
      while (businessDays < 20) {
        current.setDate(current.getDate() + 1)
        const day = current.getDay()
        if (day !== 0 && day !== 6) businessDays++
      }
      return current.toLocaleDateString(`${clientData.value.default_language || 'es'}-CL`, { timeZone: 'UTC' })
    })

    const formatDate = (dateValue) => {
      if (!dateValue) return '-'
      const raw = dateValue?.$date || dateValue
      return new Date(raw).toLocaleDateString(`${clientData.value.default_language || 'es'}-CL`, { timeZone: 'UTC' })
    }

    const getStageVariant = (stage) => {
      const variants = {
        reception: 'light-info',
        identity_validation: 'light-warning',
        analysis: 'light-primary',
        execution: 'light-info',
        response: 'light-success',
        closure: 'light-secondary',
      }
      return variants[stage] || 'light-info'
    }

    const getStatusVariant = (status) => {
      const variants = {
        open: 'light-info',
        resolved: 'light-success',
        rejected: 'light-danger',
      }
      return variants[status] || 'light-secondary'
    }

    const submitFollowUp = async () => {
      isSubmitting.value = true
      try {
        const item = await getItem({
          collection: 'arsopbl',
          query: {
            caseNumber: caseNumber.value,
            followUpToken: followUpToken.value,
          },
        })

        if (!item) throw new Error('not_found')

        requestData.value = {
          caseNumber: item.caseNumber,
          rightCode: item.rightCode,
          createdAt: item.createdAt,
          stage: item.stage,
          status: item.status,
          formalResponse: item.formalResponse || null,
        }
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('arsopbl.error_follow_up'))
      } finally {
        isSubmitting.value = false
      }
    }

    const goToNewRequest = () => {
      const routeData = router.resolve({ name: 'arsopbl-new', params: { clientPublicId } })
      window.open(routeData.href, '_blank')
    }

    onMounted(async () => {
      try {
        if (!clientPublicId) throw new Error('Client public id not present')

        const item = await getItem({ collection: 'client', query: { publicId: Number(clientPublicId) } })
        if (!item) throw new Error('Client not found')

        item._id = item._id.toString()
        clientData.value = item

        i18n.locale = clientData.value.default_language || 'es'
        localize(clientData.value.default_language || 'es')

        // Auto-submit if token in URL
        if (route.value.query?.token && route.value.query?.case) {
          caseNumber.value = route.value.query.case
          followUpToken.value = route.value.query.token
          await submitFollowUp()
        }
      } catch (error) {
        console.log(error)
        clientNotFound.value = true
      } finally {
        isLoading.value = false
      }
    })

    return {
      clientData,
      clientNotFound,
      isLoading,
      isSubmitting,
      caseNumber,
      followUpToken,
      requestData,
      required,
      bgImg,
      estimatedResponseDate,
      formatDate,
      getStageVariant,
      getStatusVariant,
      submitFollowUp,
      goToNewRequest,
    }
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/pages/page-auth.scss';
.custom-h2 {
  padding-left: 0.5rem !important;
  padding-bottom: 0;
  margin-bottom: 0;
}
.custom-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  overflow: hidden;
}
.img-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}
.card-width {
  max-width: 800px !important;
  margin: 0 auto;
}
.status-container {
  max-width: 900px;
  margin: 2rem auto;
  padding: 0 1rem;
}
@media (min-width: 768px) {
  .responsive-border {
    border-right: 1px solid #dee2e6;
  }
}
</style>
